import { useIsExpiredCutOff } from '../../hooks/useIsExpiredCutOff'
import { CutOffHoverConsumer, CutOffHoverProvider } from '../../context/CutOffHoverContext'
import { VStack } from '@musinsa/mwms-ds'
import { CELL_WIDTH, TIME_CELL_WIDTH } from '../../constants'
import Text from '@/components/content/Text'
import styled from 'styled-components'
import { BaseCell } from '../shared/BaseCell'
import { colors, colorSystem } from '@/styles/theme'
import { CutOffCutTypeCell } from './CutOffCutTypeCell'
import { CutOffCell } from './CutOffCell'
import { RefObject } from 'react'
import { TimeSlot } from '../../hooks/useGetTimeSlots'
import { CutOffRowDateTimeProvider } from '../../context/CutOffRowDateTimeContext'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { useHoveredWarehouse } from '../../hooks/useHoveredWarehouse'
import dayjs from 'dayjs'

type Props = {
  data: TimeSlot
  scrollContainerRef: RefObject<HTMLDivElement>
  warehouses: { warehouseCode: string; warehouseName: string }[]
}

export function CutOffRow({ data, scrollContainerRef, warehouses }: Props) {
  const { date, startTime, endTime, warehouses: slotWarehouses, waitTimeMinutes } = data
  const time = startTime.slice(0, 5) // HH:mm (context/표시용)

  const { data: cutOffWarehousesData } = useGetCutOffWarehouses()
  const mwmsWarehouseCodes = cutOffWarehousesData?.filter((warehouse) => warehouse.module === 'MWMS').map((warehouse) => warehouse.warehouseCode)

  const [hoveredWarehouse, setHoveredWarehouse] = useHoveredWarehouse()

  const hasB2cSchedules = slotWarehouses.some((w) => mwmsWarehouseCodes?.includes(w.warehouseCode))

  const isExpired = useIsExpiredCutOff({ date, time })
  // 현재 시각의 시(hour)와 동일한 슬롯 강조 (1분 단위 isExpired 업데이트로 자연스럽게 재평가됨)
  const isCurrent = dayjs().isSame(dayjs(`${date} ${time}`), 'hour')

  // 1시간 단위 슬롯 → "HH:00 ~ (HH+1):00" 범위로 표기
  const timeRangeLabel = `${time} ~ ${endTime.slice(0, 5)}`

  const timeTextColor = isCurrent ? colors.white : isExpired ? colorSystem.text.common.tertiary : colorSystem.text.common.primary

  return (
    <CutOffRowDateTimeProvider date={date} time={time}>
      <CutOffHoverProvider>
        <CutOffHoverConsumer>
          {({ isCutOffHover, setIsCutOffHover }) => (
            <RowWrap
              key={time}
              $isCurrent={isCurrent}
              $isExpired={isExpired}
              onMouseEnter={() => setIsCutOffHover(true)}
              onMouseLeave={() => setIsCutOffHover(false)}
            >
              <CutOffLeftWrap $isCurrent={isCurrent} $isExpired={isExpired} style={{ height: 42 }}>
                <TimeCell $isExpired={isExpired} $isCutOffHover={isCutOffHover} $isCurrent={isCurrent}>
                  <Text kind="Body_14_medium" color={timeTextColor} style={{ whiteSpace: 'nowrap' }}>
                    {timeRangeLabel}
                  </Text>
                </TimeCell>
                <CutOffCutTypeCell
                  scrollContainerRef={scrollContainerRef}
                  hasSchedules={hasB2cSchedules}
                  schedule={data}
                  waitTime={waitTimeMinutes ?? 60}
                  isCurrent={isCurrent}
                  isExpired={isExpired}
                />
              </CutOffLeftWrap>

              {warehouses?.map(({ warehouseCode, warehouseName }) => {
                const isColumnHover = hoveredWarehouse === warehouseCode

                const slotWarehouse = slotWarehouses.find((w) => w.warehouseCode === warehouseCode)

                return (
                  <VStack
                    key={warehouseCode}
                    style={{ minWidth: CELL_WIDTH }}
                    onMouseEnter={() => setHoveredWarehouse(warehouseCode)}
                    onMouseLeave={() => setHoveredWarehouse(null)}
                  >
                    {slotWarehouse ? (
                      <CutOffCell
                        data={slotWarehouse}
                        warehouseName={warehouseName}
                        startTime={startTime}
                        endTime={endTime}
                        isCurrent={isCurrent}
                        isExpired={isExpired}
                        isColumnHover={isColumnHover}
                      />
                    ) : (
                      <NotOperationCell
                        $isCutOffHover={isCutOffHover}
                        $isCurrent={isCurrent}
                        $isExpired={isExpired}
                        $isColumnHover={isColumnHover}
                      />
                    )}
                  </VStack>
                )
              })}
            </RowWrap>
          )}
        </CutOffHoverConsumer>
      </CutOffHoverProvider>
    </CutOffRowDateTimeProvider>
  )
}

const RowWrap = styled.div<{ $isCurrent: boolean; $isExpired: boolean }>`
  min-width: fit-content;
  display: flex;
  background-color: ${({ $isCurrent, $isExpired }) => ($isCurrent ? tokens.layerDesktop['bg-accent'] : $isExpired ? colors.gray050 : 'transparent')};
`

const CutOffLeftWrap = styled.div<{ $isCurrent: boolean; $isExpired: boolean }>`
  min-width: var(--left-sticky-width);
  background-color: ${({ $isCurrent, $isExpired }) => ($isCurrent ? tokens.layerDesktop['bg-accent'] : $isExpired ? colors.gray050 : colors.white)};
  z-index: 9;

  position: sticky;
  left: 0;

  display: flex;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background-color: ${colors.gray400};
    pointer-events: none;
  }
`

const TimeCell = styled(BaseCell)<{ $isExpired: boolean; $isCutOffHover: boolean; $isCurrent: boolean }>`
  width: ${TIME_CELL_WIDTH}px;
  background-color: ${({ $isCurrent, $isCutOffHover }) => ($isCurrent ? 'transparent' : $isCutOffHover ? 'rgba(36, 94, 255, 0.04)' : 'transparent')};
`

const NotOperationCell = styled(BaseCell)<{ $isCutOffHover: boolean; $isCurrent: boolean; $isExpired: boolean; $isColumnHover: boolean }>`
  flex: 1;
  background-color: ${({ $isCurrent, $isCutOffHover, $isExpired, $isColumnHover }) =>
    $isCurrent ? 'transparent' : $isCutOffHover || $isColumnHover ? 'rgba(36, 94, 255, 0.04)' : $isExpired ? 'transparent' : colors.gray050};
`
